/**
 * WebSocket 工具类
 * 用于管理 WebSocket 连接
 */

class WebSocketManager {
  constructor() {
    this.ws = null
    this.reconnectTimer = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectInterval = 3000 // 3秒
    this.isManualClose = false // 是否手动关闭
  }

  /**
   * 从 baseUrl 中提取 host、port 和路径
   * @param {string} baseUrl - 例如: http://172.31.243.74:8888/apply-online-server
   * @returns {object} {host, port, path}
   */
  parseBaseUrl(baseUrl) {
    try {
      // 移除协议前缀
      let url = baseUrl.replace(/^https?:\/\//, '')
      // 提取路径部分
      const pathIndex = url.indexOf('/')
      let path = ''
      if (pathIndex !== -1) {
        path = url.substring(pathIndex) // 包含前导斜杠，例如: /apply-online-server
        url = url.substring(0, pathIndex)
      }
      // 分离 host 和 port
      const parts = url.split(':')
      const host = parts[0]
      const port = parts[1] || (baseUrl.startsWith('https') ? '443' : '80')
      return { host, port, path }
    } catch (error) {
      console.error('解析 baseUrl 失败:', error)
      return null
    }
  }

  /**
   * 构建 WebSocket URL
   * @param {string} sessionId - 会话ID
   * @returns {string} WebSocket URL
   */
  buildWebSocketUrl(sessionId) {
    if (!sessionId) {
      console.error('sessionId 不能为空')
      return null
    }

    // 获取 baseUrl
    let baseUrl = window.g?.baseUrl
    if (!baseUrl) {
      console.error('无法获取 baseUrl')
      return null
    }

    // 解析 host、port 和路径
    const { host, port, path } = this.parseBaseUrl(baseUrl)
    if (!host || !port) {
      console.error('无法解析 host 和 port')
      return null
    }

    // 构建 WebSocket URL
    // 协议：如果 baseUrl 是 https，则使用 wss，否则使用 ws
    const protocol = baseUrl.startsWith('https') ? 'wss' : 'ws'
    // 使用配置的路径（如果存在），否则使用默认值
    const contextPath = path || '/apply-online-server'
    const wsUrl = `${protocol}://${host}:${port}${contextPath}/websocket/session/${sessionId}`
    
    return wsUrl
  }

  /**
   * 连接 WebSocket
   * @param {string} sessionId - 会话ID
   * @param {function} onMessage - 消息回调函数
   * @param {function} onError - 错误回调函数
   * @param {function} onOpen - 连接成功回调函数
   * @param {function} onClose - 连接关闭回调函数
   */
  connect(sessionId, onMessage, onError, onOpen, onClose) {
    // 如果已有连接，先关闭
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket 已连接，关闭旧连接')
      this.disconnect()
    }

    const wsUrl = this.buildWebSocketUrl(sessionId)
    if (!wsUrl) {
      console.error('无法构建 WebSocket URL')
      if (onError) {
        onError(new Error('无法构建 WebSocket URL'))
      }
      return
    }

    console.log('正在连接 WebSocket:', wsUrl)

    try {
      this.ws = new WebSocket(wsUrl)
      this.isManualClose = false

      // 连接成功
      this.ws.onopen = () => {
        console.log('WebSocket 连接成功')
        this.reconnectAttempts = 0
        if (onOpen) {
          onOpen()
        }
      }

      // 接收消息
      this.ws.onmessage = (event) => {
        console.log('WebSocket 收到消息:', event.data)
        if (onMessage) {
          try {
            const data = JSON.parse(event.data)
            onMessage(data)
          } catch (e) {
            // 如果不是 JSON，直接传递原始数据
            onMessage(event.data)
          }
        }
      }

      // 连接错误
      this.ws.onerror = (error) => {
        console.error('WebSocket 连接错误:', error)
        if (onError) {
          onError(error)
        }
      }

      // 连接关闭
      this.ws.onclose = (event) => {
        console.log('WebSocket 连接关闭', event.code, event.reason)
        if (onClose) {
          onClose(event)
        }

        // 如果不是手动关闭，尝试重连
        if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++
          console.log(`WebSocket 将在 ${this.reconnectInterval / 1000} 秒后尝试重连 (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
          
          this.reconnectTimer = setTimeout(() => {
            this.connect(sessionId, onMessage, onError, onOpen, onClose)
          }, this.reconnectInterval)
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('WebSocket 重连次数已达上限')
        }
      }
    } catch (error) {
      console.error('创建 WebSocket 连接失败:', error)
      if (onError) {
        onError(error)
      }
    }
  }

  /**
   * 发送消息
   * @param {string|object} data - 要发送的数据
   */
  send(data) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket 未连接，无法发送消息')
      return false
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data)
      this.ws.send(message)
      return true
    } catch (error) {
      console.error('发送 WebSocket 消息失败:', error)
      return false
    }
  }

  /**
   * 断开连接
   */
  disconnect() {
    this.isManualClose = true
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    this.reconnectAttempts = 0
    console.log('WebSocket 已断开连接')
  }

  /**
   * 获取连接状态
   * @returns {string} CONNECTING, OPEN, CLOSING, CLOSED
   */
  getReadyState() {
    if (!this.ws) {
      return 'CLOSED'
    }
    const states = {
      0: 'CONNECTING',
      1: 'OPEN',
      2: 'CLOSING',
      3: 'CLOSED'
    }
    return states[this.ws.readyState] || 'UNKNOWN'
  }

  /**
   * 检查是否已连接
   * @returns {boolean}
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN
  }
}

// 创建单例实例
const websocketManager = new WebSocketManager()

export default websocketManager

